import type { ReactiveControllerHost } from 'lit';
import type { BaseTranslationsLoader } from '@atlas/shared/universal/i18n';
import i18next from 'i18next';
import { i18nConfig } from '@atlas/shared/client/i18n/config.ts';
import { Task, type TaskConfig } from '@lit/task';

/* eslint-disable @typescript-eslint/no-explicit-any */

export class TranslationsTask extends Task<[], unknown> {
    public serverTranslationsLoaded: Promise<void>;
    private resolveServerTranslationsLoadedPromise?: () => void;

    constructor(
        private host: ReactiveControllerHost,
        private translationsLoaderGetter: () => BaseTranslationsLoader<any>,
        taskConfig?: Omit<TaskConfig<[], unknown>, 'task'>,
        private fetchAsyncServerTranslations = false
    ) {
        super(host, {
            ...taskConfig,
            task: async ([]) => {
                if (!i18next.isInitialized) {
                    if (this.fetchAsyncServerTranslations) {
                        await this.loadTranslations({ fetchServerTranslations: false });
                        this.loadTranslations({ fetchServerTranslations: true, updateHost: true });
                    } else {
                        await this.loadTranslations({ fetchServerTranslations: true });
                    }
                    return;
                }
            }
        });

        this.serverTranslationsLoaded = new Promise((resolve) => {
            this.resolveServerTranslationsLoadedPromise = resolve;
        });
    }

    private async loadTranslations(opts: { fetchServerTranslations: boolean; updateHost?: boolean }) {
        const { fetchServerTranslations = true, updateHost = false } = opts;
        const translationsLoader = this.translationsLoaderGetter();
        const translations = await translationsLoader.getLanguageTranslations({ fetchServerTranslations });

        await this.setI18nResources(translationsLoader.config.language, translations);

        if (fetchServerTranslations) this.resolveServerTranslationsLoadedPromise?.();
        if (updateHost) this.host.requestUpdate();
    }

    private async setI18nResources(language: string, translations: any) {
        await i18next.init({
            ...i18nConfig,
            lng: language,
            resources: {
                [language]: translations
            }
        });
    }
}
